import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Checkbox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Input,
  Textarea,
  Typography,
} from "@material-tailwind/react";
import React, {useCallback, useEffect, useState} from "react";
import Dict from "../../helper/dict";
import SelectInput from "../system/select";
import {bankPayment} from "../../services/payment/payment";
import {getBankAccounts} from "../../services/bank_account/bank_account";
import {BsClockHistory, BsDatabaseDown, BsDatabaseUp, BsWallet2} from "react-icons/bs";
import persianNumberFormat from "../../helper/PersianNumberFormat";
import WalletReservation from "../system/walletReservation";
import {getUserWallet, getWithdrawalAvailableSources, submitWalletWithdrawal} from "../../services/wallet/wallet";
import {WalletChangesGrid} from "./grid";
import axios from "axios";
import {getBaseUrl} from "../../config"
import {getBackOfficeWallet} from "../../services/back_office/wallet";
import {toast} from "react-toastify";
import PersianDateTime from "../system/persianDateTime";
import {
  DEFAULT_PAYMENT_METHOD,
  PAYMENT_GATEWAY_OPTIONS,
  redirectToPaymentLink
} from "../../helper/paymentGateway";
import {getWithdrawalErrorMessage, WITHDRAWAL_MESSAGES} from "../../helper/withdrawalMessages";

const MIN_WITHDRAWAL_AMOUNT = 10000000;

const getErrorMessage = (result) => {
  return result?.data?.error || result?.data?.message || "";
};

export function Wallet({person_id}) {

  const [personId, setPersonId] = useState(person_id)
  const [wallet, setWallet] = useState({});
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletReservationBalance, setWalletReservationBalance] = useState(0);
  const [walletWithdrawalBalance, setWalletWithdrawalBalance] = useState(0);

  const [roleId, setRoleId] = useState(3)

  const [walletChanges, setWalletChanges] = useState([])

  const [depositValue, setDepositValue] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(DEFAULT_PAYMENT_METHOD);
  const [withdrawalAccountId, setWithdrawalAccountId] = useState(null)
  const [withdrawalDescription, setWithdrawalDescription] = useState("");
  const [availableSources, setAvailableSources] = useState([]);
  const [selectedWalletTransactionIds, setSelectedWalletTransactionIds] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [withdrawalAccessDenied, setWithdrawalAccessDenied] = useState(false);
  const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);
  const [hoveredWithdrawalSourceGroupKey, setHoveredWithdrawalSourceGroupKey] = useState(null);

  const [reserves, setReserves] = useState([]);

  useEffect(() => {
    setPersonId(person_id)
  }, [person_id]);

  const getData = useCallback(async () => {
    let result;
    if (!personId) {
      result = await getUserWallet();
    } else {
      result = await getBackOfficeWallet(personId)
    }
    if (result.status === 200) {
      const {user_wallet, wallet_changes, wallet_reservation_log, role_id} = result.data;
      setWallet(user_wallet)
      setWalletChanges(wallet_changes || [])
      setReserves(wallet_reservation_log || [])
      setRoleId(role_id);
    }
  }, [personId]);

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    setWalletBalance(wallet?.balance ?? 0)
    setWalletReservationBalance(wallet?.reservation_balance ?? 0)
    setWalletWithdrawalBalance(wallet?.withdrawal_balance ?? 0)
  }, [wallet])

  const bank_payment = async () => {
    const data = {
      amount: parseInt(depositValue),
      description: "wallet",
      payment_method: paymentMethod
    };

    let result;
    result = await bankPayment(data)
    if (result.status === 200) {
      const {id} = result.data;
      localStorage.setItem("transaction_id", id)
      redirectToPaymentLink(result.data);
    }
  };

  const loadAvailableSources = useCallback(async () => {
    try {
      setLoadingSources(true);
      const result = await getWithdrawalAvailableSources();
      const message = getErrorMessage(result);
      if (result.status === 200) {
        setAvailableSources(result.data?.sources || []);
        setWithdrawalAccessDenied(false);
      } else if (message === "Only agents can withdraw") {
        setWithdrawalAccessDenied(true);
      } else {
        toast.error("خطا در دریافت درآمدهای قابل برداشت");
      }
    } finally {
      setLoadingSources(false);
    }
  }, []);

  const loadBankAccounts = useCallback(async () => {
    const result = await getBankAccounts();
    if (result.status === 200) {
      const data = result.data;
      const accounts = Object.values(data).find(item => Array.isArray(item)) || [];
      setBankAccounts(accounts);
    }
  }, []);

  useEffect(() => {
    if (!personId && roleId === 2) {
      loadAvailableSources();
      loadBankAccounts();
    }
  }, [personId, roleId, loadAvailableSources, loadBankAccounts]);

  const selectedSources = availableSources.filter(source =>
    selectedWalletTransactionIds.includes(source.wallet_transaction_id)
  );
  const selectedTotal = selectedSources.reduce((sum, source) => sum + Number(source.available_amount || source.amount || 0), 0);
  const selectedBankAccount = bankAccounts.find(account => String(account.id) === String(withdrawalAccountId));
  const canSubmitWithdrawal = selectedWalletTransactionIds.length > 0 && selectedTotal >= MIN_WITHDRAWAL_AMOUNT && Boolean(withdrawalAccountId) && !submittingWithdrawal;
  const groupedAvailableSources = Object.values(availableSources.reduce((groups, source) => {
    const productId = source?.service_id || source?.reservation_id || source?.product_id || "unknown";
    const productType = source?.product_type || "unknown";
    const key = `${productType}-${productId}`;

    if (!groups[key]) {
      groups[key] = {
        key,
        productId,
        productType,
        rows: [],
        total: 0,
      };
    }

    groups[key].rows.push(source);
    groups[key].total += Number(source?.available_amount || source?.amount || 0);
    return groups;
  }, {}));

  const toggleSource = (walletTransactionId) => {
    setSelectedWalletTransactionIds(prev =>
      prev.includes(walletTransactionId)
        ? prev.filter(id => id !== walletTransactionId)
        : [...prev, walletTransactionId]
    );
  };

  const clearWithdrawalSelection = () => {
    setSelectedWalletTransactionIds([]);
  };

  const renderSourceType = (source) => {
    if (source.product_type === "service" && source.source_type === "agent_share") {
      return "درآمد خدمت";
    }
    if (source.product_type && source.source_type) {
      return `${Dict(source.product_type)} - ${Dict(source.source_type)}`;
    }
    return source.source_type ? Dict(source.source_type) : "-";
  };

  const renderProductLabel = (productType) => {
    if (productType === "service") return "خدمت";
    if (productType === "reservation" || productType === "consultation") return "رزرو";
    return productType && productType !== "unknown" ? Dict(productType) : "بدون منبع";
  };

  const withdrawal = async () => {
    if (!withdrawalAccountId) {
      toast.error("حساب بانکی خود را انتخاب کنید")
      return
    }
    if (selectedWalletTransactionIds.length === 0) {
      toast.error("حداقل یک درآمد را انتخاب کنید")
      return
    }
    if (selectedTotal < MIN_WITHDRAWAL_AMOUNT) {
      toast.error(WITHDRAWAL_MESSAGES.minimumAmount)
      return;
    }

    const data = {
      account_id: parseInt(withdrawalAccountId),
      wallet_transaction_ids: selectedWalletTransactionIds,
    };
    if (withdrawalDescription?.trim()) {
      data.description = withdrawalDescription.trim();
    }

    setSubmittingWithdrawal(true);
    const result = await submitWalletWithdrawal(data)
    setSubmittingWithdrawal(false);

    if (result.status >= 200 && result.status < 300) {
      clearWithdrawalSelection();
      setWithdrawalDescription("");
      await getData();
      await loadAvailableSources();
      toast.success("درخواست برداشت با موفقیت ثبت شد");
      setWithdrawalModalOpen(false);
      return;
    }

    const message = getErrorMessage(result);
    if (message === "Some selected wallet transactions are invalid or already withdrawn") {
      await loadAvailableSources();
      clearWithdrawalSelection();
      toast.error(WITHDRAWAL_MESSAGES.pendingCustomerPayment);
    } else if (message === "Only agents can withdraw") {
      setWithdrawalAccessDenied(true);
    } else {
      toast.error(getWithdrawalErrorMessage(message));
    }
  };

  const downloadFile = async () => {
    try {

      let response;
      if (!personId) {
        response = await axios.get(getBaseUrl() + `/person_wallet_change`, {
          responseType: 'blob', // Important to set responseType to 'blob' for file downloads
        });
      } else {
        response = await axios.get(getBaseUrl() + `/back_office_wallet_change/${personId}`, {
          responseType: 'blob', // Important to set responseType to 'blob' for file downloads
        });
      }

      // Step 2: Handle the response as a Blob (binary data)
      const blob = response.data;

      // Step 3: Create a URL for the Blob object
      const downloadUrl = window.URL.createObjectURL(blob);

      // Step 4: Create an anchor element and trigger a download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'wallet_changes.xlsx'; // Set default file name
      document.body.appendChild(link);
      link.click();

      // Clean up: remove the link element after download
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl); // Release the object URL

    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };


  return (
    <div className="flex flex-col w-full">

      <div className="flex flex-col w-full md:flex-row gap-8">

        <div className="flex flex-col w-full md:flex-col gap-y-8">

          <div className="flex flex-row">

            <Card className="w-full">
              <div className="flex flex-row align-baseline">
                <BsWallet2 className="mx-3" size="3em" color="blue-gray"/>
                <Typography variant="h5" className="mt-3" color="blue-gray">
                  کیف پول
                </Typography>
              </ div>
              <CardBody>
                <div className="flex flex-row gap-x-2 my-2 align-middle">
                  <Typography color="blue-gray">
                    موجودی:
                  </Typography>
                  <Typography color="blue-gray">
                    {persianNumberFormat(walletBalance, true) + " ریال"}
                  </Typography>
                </div>
                {walletReservationBalance > 0 &&
                  <div className="flex flex-row gap-x-2 my-2 align-middle">
                    <Typography color="blue-gray">
                      رزرو شده:
                    </Typography>
                    <Typography color="blue-gray">
                      {persianNumberFormat(walletReservationBalance, true) + " ریال"}
                    </Typography>
                  </div>
                }
                {walletWithdrawalBalance > 0 &&
                  <div className="flex flex-row gap-x-2 my-2 align-middle">
                    <Typography color="blue-gray">
                      درخواست برداشت:
                    </Typography>
                    <Typography color="blue-gray">
                      {persianNumberFormat(walletWithdrawalBalance, true) + " ریال"}
                    </Typography>
                  </div>
                }
              </CardBody>
            </Card>

          </div>

          <div className="flex flex-row">


            <Card className="w-full">
              <CardBody>
                <div className="mb-4 flex items-center justify-between">
                  <Typography variant="h5" color="blue-gray" className="">
                    رزروهای من
                  </Typography>
                  <Typography
                    as="a"
                    href="#"
                    variant="small"
                    color="blue"
                    className="font-bold"
                  >
                    همه
                  </Typography>
                </div>
                <div className="divide-y divide-gray-200">
                  {reserves &&
                    <WalletReservation reserves={reserves}/>
                  }
                </div>
              </CardBody>
            </Card>

          </div>

        </div>

        {!personId &&
          <div className="flex flex-col w-full md:flex-col gap-y-8">

            <div className="flex flex-row w-full">
              <Card className="w-full">
                <CardBody>
                  <div className="flex flex-row align-baseline">
                    <BsDatabaseUp className="mx-3" size="2em"/>
                    <Typography variant="h5" className="mt-3" color="blue-gray">
                      شارژ حساب
                    </Typography>
                  </ div>

                  <div className="my-4">
                    <Input variant="standard"
                           label={Dict("deposit_number")}
                           placeholder={Dict("deposit_number")}
                           type={"number"}
                           value={depositValue}
                           step="10000"
                           onChange={(e) => {
                             setDepositValue(e.target.value)
                           }}
                    />
                    {depositValue > 0 &&
                      <Typography color="blue-gray" className="flex w-full my-4">
                        {"مبلغ شارژ کیف پول: " + persianNumberFormat(depositValue, true) + " ریال "}
                      </Typography>
                    }
                  </div>
                  <div className="my-4">
                    <SelectInput
                      label="درگاه پرداخت"
                      defaultValue={paymentMethod}
                      refreshSignal={paymentMethod}
                      options={PAYMENT_GATEWAY_OPTIONS}
                      onChange={setPaymentMethod}
                    />
                  </div>
                </CardBody>
                <CardFooter className="textLeft">
                  <Button variant="gradient" onClick={bank_payment}>
                    شارژ
                  </Button>
                </CardFooter>

              </Card>
            </div>

            {roleId === 2 &&
              <div className="flex flex-row w-full">
                <Card className="w-full">
                  <CardBody>
                    <div className="mb-4 flex flex-row align-baseline">
                      <BsDatabaseDown className="mx-3" size="2em"/>
                      <Typography variant="h5" className="mt-3" color="blue-gray">
                        برداشت از حساب
                      </Typography>
                    </ div>

                    {withdrawalAccessDenied ? (
                      <Typography color="blue-gray" className="py-4 text-center">
                        فقط مشاوران امکان ثبت درخواست برداشت دارند.
                      </Typography>
                    ) : (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded border border-blue-gray-50 p-3">
                          <Typography color="gray" variant="small">درآمدهای قابل برداشت</Typography>
                          <Typography color="blue-gray" className="mt-1 font-bold">
                            {loadingSources ? "در حال دریافت..." : persianNumberFormat(availableSources.length, true)}
                          </Typography>
                        </div>
                        <div className="rounded border border-blue-gray-50 p-3">
                          <Typography color="gray" variant="small">جمع انتخاب شده</Typography>
                          <Typography color="blue-gray" className="mt-1 font-bold">
                            {persianNumberFormat(selectedTotal, true) + " ریال"}
                          </Typography>
                        </div>
                      </div>
                    )}
                  </CardBody>
                  <CardFooter className="flex justify-end border-t">
                    {!withdrawalAccessDenied &&
                      <Button variant="gradient" onClick={() => setWithdrawalModalOpen(true)}>
                        ثبت درخواست برداشت
                      </Button>
                    }
                  </CardFooter>
                </Card>

                <Dialog
                  open={withdrawalModalOpen}
                  handler={() => setWithdrawalModalOpen(!withdrawalModalOpen)}
                  size="xl"
                  animate={{mount: {scale: 1, y: 0}, unmount: {scale: 0.9, y: -100}}}
                >
                  <DialogHeader className="border-b">
                    <Typography variant="h6" color="blue-gray">ثبت درخواست برداشت</Typography>
                  </DialogHeader>
                  <DialogBody className="max-h-[78vh] overflow-y-auto">
                    <div className="flex flex-col gap-4">
                      <SelectInput
                        className="w-full my-4"
                        label={Dict("bank_accounts")}
                        defaultValue={withdrawalAccountId}
                        refreshSignal={withdrawalAccountId}
                        autoLoadService={getBankAccounts}
                        autoLoadRender={({title, card}) => {
                          return title + " - " + persianNumberFormat(card)
                        }}
                        onChange={setWithdrawalAccountId}
                      />

                      <Textarea
                        label="توضیحات"
                        value={withdrawalDescription}
                        onChange={(event) => setWithdrawalDescription(event.target.value)}
                      />

                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[760px] border-separate border-spacing-0 text-left">
                          <thead className="sticky top-0 z-10 bg-white">
                          <tr className="border-b border-blue-gray-100">
                            <th className="w-12 px-3 py-2 text-center"></th>
                            <th className="px-3 py-2 text-center"><Typography className="text-xs font-semibold text-blue-gray-500">مبلغ قابل برداشت</Typography></th>
                            <th className="px-3 py-2 text-center"><Typography className="text-xs font-semibold text-blue-gray-500">نوع درآمد</Typography></th>
                            <th className="px-3 py-2 text-center"><Typography className="text-xs font-semibold text-blue-gray-500">تاریخ تسویه</Typography></th>
                            <th className="px-3 py-2 text-center"><Typography className="text-xs font-semibold text-blue-gray-500">تراکنش</Typography></th>
                          </tr>
                          </thead>
                          <tbody>
                          {loadingSources && (
                            <tr>
                              <td colSpan={5} className="p-6 text-center">
                                <Typography color="blue-gray">در حال دریافت درآمدها...</Typography>
                              </td>
                            </tr>
                          )}
                          {!loadingSources && availableSources.length === 0 && (
                            <tr>
                              <td colSpan={5} className="p-6 text-center">
                                <Typography color="blue-gray">درآمد قابل برداشتی وجود ندارد</Typography>
                              </td>
                            </tr>
                          )}
                          {!loadingSources && groupedAvailableSources.map((group, groupIndex) => {
                            const isGrouped = group.rows.length > 1;
                            const isGroupHovered = hoveredWithdrawalSourceGroupKey === group.key;
                            const firstSource = group.rows[0];
                            const groupSourceId = firstSource?.service_id || firstSource?.reservation_id || firstSource?.product_id;
                            const groupBg = isGroupHovered ? "bg-blue-gray-50" : "bg-white";

                            return (
                              <React.Fragment key={group.key}>
                                {groupIndex > 0 && (
                                  <tr>
                                    <td colSpan={5} className="h-2 border-0 bg-transparent p-0"/>
                                  </tr>
                                )}
                                {isGrouped && (
                                  <tr
                                    className={`${groupBg} transition-colors`}
                                    onMouseEnter={() => setHoveredWithdrawalSourceGroupKey(group.key)}
                                    onMouseLeave={() => setHoveredWithdrawalSourceGroupKey(null)}
                                  >
                                    <td colSpan={5} className="rounded-t border-x border-t border-blue-gray-300 border-r-2 px-3 py-2">
                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 textRight">
                                        <Typography color="blue-gray" variant="small" className="font-semibold">
                                          {renderProductLabel(group.productType)}
                                        </Typography>
                                        {groupSourceId && (
                                          <Typography color="blue-gray" variant="small" className="font-semibold tabular-nums">
                                            {"#" + persianNumberFormat(groupSourceId, true)}
                                          </Typography>
                                        )}
                                        {firstSource?.field_title && (
                                          <Typography color="gray" variant="small">{firstSource.field_title}</Typography>
                                        )}
                                        {firstSource?.branch_title && (
                                          <Typography color="gray" variant="small">{firstSource.branch_title}</Typography>
                                        )}
                                        <div className="flex-1"/>
                                        <Typography color="gray" variant="small">
                                          {persianNumberFormat(group.rows.length, true) + " درآمد"}
                                        </Typography>
                                        <Typography color="blue-gray" variant="small" className="font-semibold tabular-nums">
                                          {persianNumberFormat(group.total, true) + " ریال"}
                                        </Typography>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {group.rows.map((source, index) => {
                                  const walletTransactionId = source.wallet_transaction_id;
                                  const checked = selectedWalletTransactionIds.includes(walletTransactionId);
                                  const sourceDate = source.settled_at || source.created_at;
                                  const sourceId = source.service_id || source.reservation_id || source.product_id;
                                  const isLastInGroup = index === group.rows.length - 1;
                                  const isSingle = !isGrouped;
                                  const frameTop = isSingle ? "border-t border-blue-gray-300" : "";
                                  const frameBottom = isLastInGroup ? "border-b border-blue-gray-300" : "border-b border-blue-gray-100";
                                  const firstCellFrame = `${frameTop} ${frameBottom} border-r-2 border-r-blue-gray-300 ${isSingle ? "rounded-r" : isLastInGroup ? "rounded-br" : ""}`;
                                  const middleCellFrame = `${frameTop} ${frameBottom}`;
                                  const lastCellFrame = `${frameTop} ${frameBottom} border-l border-l-blue-gray-300 ${isSingle ? "rounded-l" : isLastInGroup ? "rounded-bl" : ""}`;

                                  return (
                                    <tr
                                      key={`${walletTransactionId || "source"}-${group.key}-${index}`}
                                      className={`cursor-pointer transition-colors duration-150 ${groupBg}`}
                                      onMouseEnter={() => setHoveredWithdrawalSourceGroupKey(group.key)}
                                      onMouseLeave={() => setHoveredWithdrawalSourceGroupKey(null)}
                                      onClick={() => toggleSource(walletTransactionId)}
                                    >
                                      <td className={`${firstCellFrame} px-3 py-2 text-center`}>
                                        <Checkbox
                                          checked={checked}
                                          onChange={() => toggleSource(walletTransactionId)}
                                          onClick={(event) => event.stopPropagation()}
                                          crossOrigin={undefined}
                                        />
                                      </td>
                                      <td className={`${middleCellFrame} px-3 py-2 text-center`}>
                                        <Typography color="blue-gray" className="font-semibold tabular-nums">
                                          {persianNumberFormat(source.available_amount, true)}
                                          <span className="mr-1 text-xs font-normal text-blue-gray-400">ریال</span>
                                        </Typography>
                                      </td>
                                      <td className={`${middleCellFrame} px-3 py-2 text-center`}>
                                        <Typography color="blue-gray" variant="small">
                                          {renderSourceType(source)}
                                        </Typography>
                                        {!isGrouped && (sourceId || source.field_title) && (
                                          <Typography color="gray" variant="small" className="mt-0.5 tabular-nums opacity-60">
                                            {[
                                              sourceId ? `${renderProductLabel(group.productType)} #${persianNumberFormat(sourceId, true)}` : null,
                                              source.field_title,
                                              source.branch_title,
                                            ].filter(Boolean).join(" · ")}
                                          </Typography>
                                        )}
                                      </td>
                                      <td className={`${middleCellFrame} px-3 py-2 text-center`}>
                                        <Typography color="blue-gray" variant="small" dir="ltr" className="tabular-nums">
                                          <PersianDateTime value={sourceDate}/>
                                        </Typography>
                                      </td>
                                      <td className={`${lastCellFrame} px-3 py-2 text-center`}>
                                        <Typography color="blue-gray" variant="small" className="font-semibold tabular-nums">
                                          {walletTransactionId ? persianNumberFormat(walletTransactionId, true) : "-"}
                                        </Typography>
                                        {source.wallet_product_log_id &&
                                          <Typography color="gray" variant="small" className="opacity-60">
                                            {"log " + persianNumberFormat(source.wallet_product_log_id, true)}
                                          </Typography>
                                        }
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </DialogBody>
                  <DialogFooter className="sticky bottom-0 z-10 flex-col gap-3 border-t bg-white md:flex-row md:items-center md:justify-between">
                    <div className="grid w-full grid-cols-1 gap-2 textRight md:grid-cols-3">
                      <Typography color="blue-gray">
                        {"تعداد انتخاب شده: " + persianNumberFormat(selectedWalletTransactionIds.length, true)}
                      </Typography>
                      <Typography color="blue-gray">
                        {"جمع انتخاب شده: " + persianNumberFormat(selectedTotal, true) + " ریال"}
                      </Typography>
                      <Typography color="blue-gray" className="truncate">
                        {"حساب مقصد: " + (selectedBankAccount ? `${selectedBankAccount.title} - ${persianNumberFormat(selectedBankAccount.card)}` : "-")}
                      </Typography>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button variant="text" color="gray" onClick={() => setWithdrawalModalOpen(false)}>
                        بستن
                      </Button>
                      <Button variant="gradient" onClick={withdrawal} disabled={!canSubmitWithdrawal}>
                        {submittingWithdrawal ? "در حال ثبت..." : "ثبت درخواست"}
                      </Button>
                    </div>
                  </DialogFooter>
                </Dialog>
              </div>
            }
          </div>
        }

      </div>

      <div className="flex flex-col mt-8 w-full md:flex-row">

        <div className="w-full">

          <Card className="max-w-full">
            <CardBody className="w-full overflow-x-scroll">
              <div className="flex flex-row max-w-full align-baseline justify-between">
                <div className="flex flex-row align-baseline">
                  <BsClockHistory className="flex flex-col mx-3" size="2em"/>
                  <Typography variant="h5" className="mt-3" color="blue-gray">
                    سابقه تراکنش‌ها
                  </Typography>
                </div>
                {walletChanges.length > 0 &&
                  <Button size="sm" variant="outlined" onClick={() => {
                    downloadFile("person_wallet_change", null, "wallet_changes")
                  }}>
                    خروجی اکسل
                  </Button>
                }
              </ div>
              <WalletChangesGrid walletChanges={walletChanges}/>
            </CardBody>

          </Card>

        </div>

      </div>

    </div>
  );
}
